/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { init, send } from 'emailjs-com';
import Navbar from './Navbar';
import Footer from './Footer';
import LanguageToggle from './LanguageToggle';
import { getStateForCity, getCityUrl, getNormalizedCityName } from '../data/citiesData';
import { getLanguageForState } from '../data/languageData';
import { getCityContent } from '../data/cityContentData';
import { TbHome, TbBuilding, TbBriefcase, TbTools } from 'react-icons/tb';

// Initialize EmailJS with your user ID
init("d1zH6UNPIGfFtdCEu");

const CityLandingPage = () => {
  const { cityName } = useParams();
  const [currentLang, setCurrentLang] = useState('en');
  
  // Convert URL param to city name (e.g., "mysore" -> "Mysore", "new-delhi" -> "New Delhi")
  const cityFromUrl = cityName ? cityName.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ') : '';
  
  // Get normalized city name (exact match from data)
  const city = getNormalizedCityName(cityFromUrl) || cityFromUrl;
  
  const state = getStateForCity(city);
  const langData = getLanguageForState(state);
  const cityContent = getCityContent(city, currentLang);
  
  // Translation function
  const t = (key) => {
    if (currentLang === 'en' || !langData) {
      return key;
    }
    return langData.translations[key] || key;
  };

  // Scroll to top when component mounts or city changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cityName]);

  const [formData, setFormData] = useState({
    category: 'residential',
    name: '',
    email: '',
    phone: '',
    preferredTime: '',
    message: '',
    contactMethod: 'callback',
    pincode: '',
    city: city,
    monthlyBill: '',
    societyName: '',
    designation: '',
    agmApproval: '',
    companyName: '',
    averageBill: '',
    utilityCompanyName: ''
  });

  // Update city field when navigating between different city pages
  useEffect(() => {
    setFormData(prevData => ({
      ...prevData,
      city: city
    }));
  }, [city, cityName]);

  const timeSlots = [
    '9:00 AM - 10:00 AM',
    '10:00 AM - 11:00 AM',
    '11:00 AM - 12:00 PM',
    '2:00 PM - 3:00 PM',
    '3:00 PM - 4:00 PM',
    '4:00 PM - 5:00 PM',
    '5:00 PM - 6:00 PM',
    '6:00 PM - 7:00 PM'
  ];

  // SEO optimization - Update meta tags and structured data for this page
  useEffect(() => {
    const cityUrl = getCityUrl(city);
    const fullUrl = `https://lunarelectric.in${cityUrl}`;
    const keywords = cityContent?.keywords || `solar panel installation ${city}, solar ${city}, rooftop solar ${city}, solar energy ${city}, solar power ${city}, lunar electric ${city}`;
    // Ensure description starts with capital letter
    const rawDescription = cityContent?.description || `Professional solar panel installation services in ${city}. Get free consultation, transparent pricing, and expert solar solutions for residential and commercial projects in ${city}.`;
    const description = rawDescription.charAt(0).toUpperCase() + rawDescription.slice(1);
    
    document.title = `Solar Companies in ${city} | Solar Panel Installation ${city} | Lunar Electric ${city}`;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', `Solar Companies in ${city} | Solar Panel Installation ${city} | Lunar Electric`);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', fullUrl);
    }
    
    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', fullUrl);
    }
    
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', fullUrl);

    // Add structured data for LocalBusiness
    const existingScript = document.getElementById('city-structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredDataScript = document.createElement('script');
    structuredDataScript.id = 'city-structured-data';
    structuredDataScript.type = 'application/ld+json';
    structuredDataScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `Lunar Electric - Solar Panel Installation in ${city}`,
      "description": description,
      "url": fullUrl,
      "telephone": "+91-9148857417",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city,
        "addressRegion": state,
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "addressLocality": city,
        "addressRegion": state
      },
      "areaServed": {
        "@type": "City",
        "name": city
      },
      "serviceType": "Solar Panel Installation",
      "priceRange": "₹₹"
    });
    document.head.appendChild(structuredDataScript);

    return () => {
      const script = document.getElementById('city-structured-data');
      if (script) {
        script.remove();
      }
    };
  }, [city, cityContent, state, currentLang]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const templateParams = {
      category: formData.category,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      preferredTime: formData.preferredTime,
      message: formData.message,
      contactMethod: formData.contactMethod,
      pincode: formData.pincode,
      city: formData.city,
      monthlyBill: formData.monthlyBill || formData.averageBill,
      societyName: formData.societyName,
      designation: formData.designation,
      agmApproval: formData.agmApproval,
      companyName: formData.companyName,
      utilityCompanyName: formData.utilityCompanyName,
      pageUrl: typeof window !== 'undefined' ? window.location.href : getCityUrl(city)
    };

    try {
      await send('service_8xqj1kf', 'template_7x8k9m2', templateParams);
      alert(currentLang === 'en' ? 'Thank you! We will contact you soon.' : (langData?.translations['Thank you! We will contact you soon.'] || 'Thank you!'));
      setFormData({
        category: 'residential',
        name: '',
        email: '',
        phone: '',
        preferredTime: '',
        message: '',
        contactMethod: 'callback',
        pincode: '',
        city: city,
        monthlyBill: '',
        societyName: '',
        designation: '',
        agmApproval: '',
        companyName: '',
        averageBill: '',
        utilityCompanyName: ''
      });
    } catch (err) {
      console.error('Failed to send callback request', err);
      alert(currentLang === 'en' ? 'Failed to send callback request, please try again.' : (langData?.translations['Failed to send callback request, please try again.'] || 'Failed to send.'));
    }
  };

  return (
    <>
      <Helmet>
        <title>{cityContent?.keywords?.split(',')[0] || `Solar Companies in ${city} | Solar Panel Installation ${city} | Lunar Electric ${city}`}</title>
        <meta name="description" content={(() => {
          const rawDesc = cityContent?.description || `Professional solar panel installation services in ${city}. Get free consultation, transparent pricing, and expert solar solutions for residential and commercial projects in ${city}.`;
          return rawDesc.charAt(0).toUpperCase() + rawDesc.slice(1);
        })()} />
        <meta name="keywords" content={cityContent?.keywords || `solar panel installation ${city}, solar ${city}, rooftop solar ${city}, solar energy ${city}, solar power ${city}, lunar electric ${city}`} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:title" content={`Solar Companies in ${city} | Solar Panel Installation ${city} | Lunar Electric`} />
        <meta property="og:description" content={(() => {
          const rawDesc = cityContent?.description || `Professional solar panel installation services in ${city}. Get free consultation and expert solar solutions.`;
          return rawDesc.charAt(0).toUpperCase() + rawDesc.slice(1);
        })()} />
        <meta property="og:url" content={`https://lunarelectric.in${getCityUrl(city)}`} />
        <link rel="canonical" href={`https://lunarelectric.in${getCityUrl(city)}`} />
        {/* FAQ Schema if available */}
        {cityContent?.faqs && cityContent.faqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": cityContent.faqs.map(f => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": f.a
              }
            }))
          })}</script>
        )}
      </Helmet>
      
      <Navbar />
      <div className="callback-page">
        {state && <LanguageToggle stateName={state} currentLang={currentLang} onLangChange={setCurrentLang} />}
        
        <div className="callback-hero">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h1 className="callback-title">
                  {currentLang === 'en' ? (
                    <>
                      {t('Go Solar Today')} <span className="city-name-highlight">{t('in')} {city}</span>
                    </>
                  ) : (
                    <>
                      <span className="city-name-highlight">{city}</span> {t('Go Solar Today in')}
                    </>
                  )}
                </h1>
                <p className="callback-subtitle">{t('Get your free solar consultation - residential or commercial')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="callback-content">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6 col-md-8 callback-form-container">
                <div className="callback-form-wrapper">
                  <h2 className="form-title">{t('Get a Free Quote for Rooftop Solar')} in {city}</h2>
                  <p className="form-subtitle">{t('Schedule a FREE consultation with us today!')} - {city} Solar Installation Services</p>
                  
                  {/* Category Selection */}
                  <div className="category-selection-compact">
                    <h3 className="category-section-title">{t('Choose Your Project Type')}</h3>
                    <div className="category-options-compact">
                      <div className="category-option-compact">
                        <input
                          type="radio"
                          id="category-residential"
                          name="category"
                          value="residential"
                          checked={formData.category === 'residential'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="category-residential" className="category-label-compact">
                          <span className="category-icon-compact">
                            <TbHome size={20} />
                          </span>
                          <span className="category-text">{t('Residential')}</span>
                        </label>
                      </div>
                      <div className="category-option-compact">
                        <input
                          type="radio"
                          id="category-housing-society"
                          name="category"
                          value="housing-society"
                          checked={formData.category === 'housing-society'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="category-housing-society" className="category-label-compact">
                          <span className="category-icon-compact">
                            <TbBuilding size={20} />
                          </span>
                          <span className="category-text">{t('Housing Society')}</span>
                        </label>
                      </div>
                      <div className="category-option-compact">
                        <input
                          type="radio"
                          id="category-commercial"
                          name="category"
                          value="commercial"
                          checked={formData.category === 'commercial'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="category-commercial" className="category-label-compact">
                          <span className="category-icon-compact">
                            <TbBriefcase size={20} />
                          </span>
                          <span className="category-text">{t('Commercial')}</span>
                        </label>
                      </div>
                      <div className="category-option-compact">
                        <input
                          type="radio"
                          id="category-utility-scale"
                          name="category"
                          value="utility-scale"
                          checked={formData.category === 'utility-scale'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="category-utility-scale" className="category-label-compact">
                          <span className="category-icon-compact">
                            <TbTools size={20} />
                          </span>
                          <span className="category-text">{t('Utility Scale')}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Form */}
                  <form onSubmit={handleSubmit} className="callback-form-compact">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">{t('Full Name')} *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">{t('Phone Number')} *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    {formData.category !== 'utility-scale' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="pincode">{t('Pin Code')} *</label>
                          <input
                            type="text"
                            id="pincode"
                            name="pincode"
                            value={formData.pincode}
                            onChange={handleInputChange}
                            className="form-input"
                            pattern="[0-9]{6}"
                            title="Please enter a valid 6-digit pin code"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="city">{t('City')} *</label>
                          <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                            readOnly
                          />
                        </div>
                      </div>
                    )}

                    {formData.category === 'utility-scale' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="email">{t('Email')}</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="utilityCompanyName">{t('Company Name')}</label>
                          <input
                            type="text"
                            id="utilityCompanyName"
                            name="utilityCompanyName"
                            value={formData.utilityCompanyName}
                            onChange={handleInputChange}
                            className="form-input"
                          />
                        </div>
                      </div>
                    )}

                    {formData.category === 'residential' && (
                      <div className="form-group">
                        <label htmlFor="monthlyBill">{t('What is your average monthly electricity bill?')} *</label>
                        <select
                          id="monthlyBill"
                          name="monthlyBill"
                          value={formData.monthlyBill}
                          onChange={handleInputChange}
                          className="form-select"
                          required
                        >
                          <option value="">{t('Select your monthly bill')}</option>
                          <option value="less-than-1500">{t('Less than ₹1,500')}</option>
                          <option value="1500-2500">{t('₹1,500 - ₹2,500')}</option>
                          <option value="2500-4000">{t('₹2,500 - ₹4,000')}</option>
                          <option value="4000-8000">{t('₹4,000 - ₹8,000')}</option>
                          <option value="more-than-8000">{t('More than ₹8,000')}</option>
                        </select>
                      </div>
                    )}

                    {formData.category === 'housing-society' && (
                      <>
                        <div className="form-group">
                          <label htmlFor="societyName">{t('Name of Housing Society')} *</label>
                          <input
                            type="text"
                            id="societyName"
                            name="societyName"
                            value={formData.societyName}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="designation">{t('What is your designation in Housing Society?')} *</label>
                          <select
                            id="designation"
                            name="designation"
                            value={formData.designation}
                            onChange={handleInputChange}
                            className="form-select"
                            required
                          >
                            <option value="">{t('Select your designation')}</option>
                            <option value="management-committee">{t('Management committee member')}</option>
                            <option value="resident">{t('Resident')}</option>
                            <option value="builder">{t('Builder')}</option>
                            <option value="facility-manager">{t('Facility Manager')}</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="agmApproval">{t('AGM approval status')} *</label>
                          <select
                            id="agmApproval"
                            name="agmApproval"
                            value={formData.agmApproval}
                            onChange={handleInputChange}
                            className="form-select"
                            required
                          >
                            <option value="">{t('Select approval status')}</option>
                            <option value="approved">{t('We already have AGM approval')}</option>
                            <option value="pending">{t('We don\'t have an AGM approval yet')}</option>
                            <option value="help">{t('We want help in preparing for our AGM')}</option>
                          </select>
                        </div>
                      </>
                    )}

                    {formData.category === 'commercial' && (
                      <>
                        <div className="form-group">
                          <label htmlFor="companyName">{t('Company Name')} *</label>
                          <input
                            type="text"
                            id="companyName"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="averageBill">{t('Average Monthly Bill')} *</label>
                          <select
                            id="averageBill"
                            name="averageBill"
                            value={formData.averageBill}
                            onChange={handleInputChange}
                            className="form-select"
                            required
                          >
                            <option value="">{t('Select your average monthly bill')}</option>
                            <option value="0-50000">{t('₹0 - ₹50,000')}</option>
                            <option value="50000-2lacs">{t('₹50,000 - ₹2,00,000')}</option>
                            <option value="more-than-2lacs">{t('More than ₹2,00,000')}</option>
                          </select>
                        </div>
                      </>
                    )}

                    <div className="form-group">
                      <label htmlFor="preferredTime">{t('Preferred Time Slot')} *</label>
                      <select
                        id="preferredTime"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        className="form-select"
                        required
                      >
                        <option value="">{t('Select a time slot')}</option>
                        {timeSlots.map((slot, index) => (
                          <option key={index} value={slot}>{slot}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">{t('Message (Optional)')}</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="form-textarea"
                        rows="4"
                        placeholder={t('Tell us about your solar requirements (residential/commercial)...')}
                      />
                    </div>

                    <div className="form-submit-section">
                      <button type="submit" className="submit-btn-compact">
                        {t('Get Solar Quote')}
                      </button>
                      <a href="tel:+919148857417" className="call-now-btn">
                        📞 {t('Call Now')}
                      </a>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* City-Specific Content Section */}
        {cityContent && (
          <div className="city-specific-content">
            <div className="container">
              <div className="row">
                <div className="col-md-12">
                  <div className="city-content-wrapper">
                    <h3 className="city-content-title">
                      Solar Energy in {city}{cityContent.state && cityContent.state !== city ? `, ${cityContent.state}` : ''}
                    </h3>
                    <p className="city-content-description">{cityContent.description}</p>
                    
                    <div className="city-phrases-section">
                      <h4 className="phrases-title">Why Choose Solar in {city}?</h4>
                      <div className="phrases-grid">
                        {cityContent.phrases && cityContent.phrases.map((phrase, index) => (
                          <div key={index} className="phrase-card">
                            <p className="phrase-text">{phrase}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="city-benefits-section">
                      <h4 className="benefits-title">Benefits of Solar Installation in {city}</h4>
                      <ul className="benefits-list">
                        <li>Reduce your electricity bills by up to 90% in {city}</li>
                        <li>Government subsidies and incentives available for {city} residents</li>
                        <li>Professional installation services across {city}</li>
                        <li>Long-term savings and energy independence in {city}</li>
                        <li>Environmentally friendly solution for {city}</li>
                        <li>1-year free maintenance for all installations in {city}</li>
                      </ul>
                    </div>
                    
                    {/* Conversational City FAQs */}
                    {cityContent.faqs && cityContent.faqs.length > 0 && (
                      <div className="city-faqs-section">
                        <h4 className="faqs-title">Questions people in {city} ask us</h4>
                        <div className="faqs-list">
                          {cityContent.faqs.map((faq, idx) => (
                            <details key={idx} className="faq-item">
                              <summary className="faq-question">
                                <span className="faq-text">{faq.q}</span>
                                <span className="faq-plus-icon" aria-hidden="true"></span>
                              </summary>
                              <div className="faq-answer">
                                <p>{faq.a}</p>
                              </div>
                            </details>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="callback-cta">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h2>{t('Ready to Go Solar?')}</h2>
                <p>{t('Don\'t wait - contact us today for professional solar rooftop installations')}</p>
                <Link to="/" className="btn btn-home">
                  ← {t('Back to Home')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CityLandingPage;
